import { useCallback, useState, useEffect, useRef, useMemo } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import type { Theme } from "@mui/material";
import { Box, Typography, styled, useMediaQuery } from "@mui/material";
import { useAuth, useDoctorContext, useDocumentSignatureContext } from "@contexts";
import {
  CodeInput,
  DoctorDocumentReadinessModal,
  FullScreenLoader,
  Header,
  Modal,
  NavigateBeforeButton,
  SecondaryActionButton,
  FormBody,
  FormContentContainer,
} from "@components";
import {
  useRequestData,
  useReEnlistment,
  useRequestSigningStatus,
  useDoctorDocumentReadiness,
} from "@hooks";
import { redirectToDocumentCreationScreen } from "@utils/redirect";
import type { RequestType } from "@interfaces";
import { AuthFlow, AuthMethod, defaultPinState } from "@interfaces";
import { getFromLocalStorage, checkIfBiometricAuthIsAvailable } from "@utils";
import {
  BlockedPinDocumentSignatureErrorModal,
  UnknownDocumentCreationErrorModal,
  UnknownDocumentSignatureErrorModal,
  ValidationRequiredDocumentSignatureErrorModal,
  CertificateRevokedDocumentSignatureErrorModal,
  WrongPinDocumentSignatureErrorModal,
} from "./DocumentManipulationErrorModals";
import { MainContainer } from "./SignRecipeScreeSecondFactor.styles";
import { NavigateHomeButton } from "@components/Buttons/NavigateHomeButton";
import { useSnackbar } from "notistack";

const Title = styled(Typography)(({ theme }) => ({
  [theme.breakpoints.down("md")]: {
    display: "none",
  },
}));

const SignStepTypography = styled(Typography)(({ theme }) => ({
  fontSize: "16px",
  fontWeight: 500,
  lineHeight: "23px",
  color: theme.palette.primary.main,
}));

const PinWrapper = styled(Box)({
  display: "flex",
  flexDirection: "column",
  gap: "24px",
});

const PinLabel = styled(Typography)({
  textAlign: "center",
});

const PinContainer = styled(Box)({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  gap: "8px",
});

const StyledErrorMessage = styled(Typography)(({ theme }) => ({
  fontSize: "14px",
  color: theme.palette.error.main,
}));

const BoldText = styled("span")({
  fontWeight: 600,
});

export interface SignRecipeScreenLocationState {
  secondAuthFactorValidated?: boolean;
}

const SignRecipeScreenDefaultLocationState = {
  secondAuthFactorValidated: false,
} as const;

export function SignRecipeScreen() {
  const isDesktop = useMediaQuery((theme: Theme) => theme.breakpoints.up("md"));
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { requestId = "" } = useParams();
  const { data: requestData } = useRequestData();

  const { isSigning, isSigningOrError } = useRequestSigningStatus(
    requestData?.request.status,
  );

  const { secondAuthFactorValidated = false } =
    (useLocation().state as SignRecipeScreenLocationState) ??
    SignRecipeScreenDefaultLocationState;

  const requestType = getFromLocalStorage<RequestType>({
    key: "requestType",
  }) as RequestType;

  const { startAuthChallenge, biometricSignIn } = useAuth();
  const doctor = useDoctorContext();

  const {
    reEnlistmentMutation: { mutate: reEnlistment, isPending: isUnenrolling },
  } = useReEnlistment(requestId);

  const { ensureReadyToIssue, modalProps } = useDoctorDocumentReadiness({
    requestId,
  });

  useEffect(() => {
    ensureReadyToIssue(() => {});
  }, [ensureReadyToIssue]);

  const onReadinessModalClose = useCallback(() => {
    modalProps.onClose();
    navigate(redirectToDocumentCreationScreen(requestType, requestId), {
      replace: true,
    });
  }, [modalProps, navigate, requestType, requestId]);

  // Use the document signature context
  const {
    signPrescription,
    pinState,
    setPinState,
    isLoading: loadingSignature,
    loadingTitle,
  } = useDocumentSignatureContext();

  const [showFailedReEnlistmentModal, setShowFailedReEnlistmentModal] = useState(false);

  const [approvedBiometricSign, setApprovedBiometricSign] = useState(false);
  const [showForgottenPinModal, setShowForgottenPinModal] = useState(false);

  const [isCreatingValidationChallenge, setIsCreatingValidationChallenge] =
    useState(false);

  const secondAuthFactorRequested = useRef(false);

  const [biometricSecondFactorFailed, setBiometricSecondFactorFailed] = useState(false);

  const secondAuthFactorReady = secondAuthFactorValidated || approvedBiometricSign;

  const handleReEnlistment = useCallback(() => {
    reEnlistment(doctor, {
      onError: () => {
        setShowForgottenPinModal(false);
        setShowFailedReEnlistmentModal(true);
      },
    });
  }, [doctor, reEnlistment]);

  const handleBack = useCallback(() => {
    if (isSigning) {
      navigate("/app/solicitudes");
      enqueueSnackbar("La receta ya está en proceso de firma", {
        variant: "info",
      });

      return;
    }

    navigate(redirectToDocumentCreationScreen(requestType, requestId));
  }, [navigate, requestType, requestId, isSigning, enqueueSnackbar]);

  const askForBiometricSecondFactor = useCallback(async (): Promise<boolean> => {
    if (isSigningOrError) {
      return false;
    }

    try {
      const isBiometricAuthAvailable = await checkIfBiometricAuthIsAvailable();

      const canUseBiometricLogin = doctor.biometricIsEnabled && isBiometricAuthAvailable;

      if (canUseBiometricLogin) {
        const biometricSignInSuccess = await biometricSignIn(doctor);

        setApprovedBiometricSign(biometricSignInSuccess);

        return biometricSignInSuccess;
      }

      return false;
    } catch (error) {
      console.error("Error asking for biometric second factor", error);

      return false;
    }
  }, [doctor, biometricSignIn, isSigningOrError]);

  const askForEmailSecondFactor = useCallback(async () => {
    const authMethod = AuthMethod.EMAIL;

    navigate(`/app/solicitudes/${requestId}/receta/firma/factor-autenticacion`, {
      state: {
        authMethod,
        doctor,
      },
    });

    const authChallengeStarted = await startAuthChallenge({
      email: doctor.email,
      authMethod,
      authFlow: AuthFlow.PRESCRIPTION,
    });

    if (!authChallengeStarted) throw Error("Failed to start email auth challenge");
  }, [startAuthChallenge, navigate, requestId, doctor]);

  const handlePinChange = useCallback(
    (val: string) => {
      secondAuthFactorRequested.current = false;
      setPinState({
        pin: val,
        error: false,
        errorMessage: "",
      });
    },
    [setPinState],
  );

  useEffect(() => {
    const onCompletedPinInput = async () => {
      const hasCompletedPinInput = !!requestData && pinState.pin.length === 4;

      if (!hasCompletedPinInput) {
        return;
      }

      // If second auth factor is ready, call the signature
      if (secondAuthFactorReady && doctor.isFeaAuthenticated) {
        signPrescription(pinState.pin, requestId);

        return;
      }

      // Only ask for second auth factor once
      if (secondAuthFactorRequested.current) {
        if (biometricSecondFactorFailed) {
          await askForEmailSecondFactor();
        }

        return;
      }

      setIsCreatingValidationChallenge(true);
      secondAuthFactorRequested.current = true;
      try {
        if (await askForBiometricSecondFactor()) {
          return;
        } else {
          setBiometricSecondFactorFailed(true);
        }
      } catch (error) {
        console.error(error);
      } finally {
        setIsCreatingValidationChallenge(false);
      }
    };

    onCompletedPinInput();
  }, [
    requestData,
    pinState.pin,
    secondAuthFactorReady,
    askForBiometricSecondFactor,
    askForEmailSecondFactor,
    requestId,
    doctor.isFeaAuthenticated,
    signPrescription,
    biometricSecondFactorFailed,
  ]);

  const resetPin = useCallback(() => {
    setPinState(defaultPinState);
  }, [setPinState]);

  const resetPinAndGoHome = useCallback(() => {
    setPinState(defaultPinState);
    navigate("/app/solicitudes");
  }, [setPinState, navigate]);

  const loadingMessage = isCreatingValidationChallenge
    ? secondAuthFactorReady
      ? "Validando código"
      : "Cargando"
    : !requestData
      ? "Comprobando información"
      : loadingTitle;

  const isLoading = loadingSignature || isCreatingValidationChallenge || !requestData;

  const showHomeButton = isSigning;

  const leftButton = useMemo(() => {
    return showHomeButton ? (
      <NavigateHomeButton onClick={handleBack} />
    ) : (
      <NavigateBeforeButton onClick={handleBack} />
    );
  }, [showHomeButton, handleBack]);

  if (isLoading)
    return (
      <>
        <Header title="Firmar receta" titleColor="black" left={leftButton} />
        <FormContentContainer flex={1} disableGutters>
          <FormBody justifyContent="center">
            <FullScreenLoader title={loadingMessage} />
          </FormBody>
        </FormContentContainer>
        <DoctorDocumentReadinessModal {...modalProps} onClose={onReadinessModalClose} />
      </>
    );

  const pinMessage = secondAuthFactorReady
    ? "Vuelve a ingresar tu PIN de 4 dígitos para firmar"
    : "Ingresa tu PIN de 4 dígitos para firmar la receta";

  return (
    <>
      <Header
        title={isDesktop ? "Firmar receta" : undefined}
        useMekiDocIcon={isDesktop}
        titleColor="black"
        left={leftButton}
      />
      <FormContentContainer disableGutters>
        <FormBody>
          <Title variant="h5" fontWeight={"medium"}>
            Firmar receta
          </Title>
          {!secondAuthFactorReady && (
            <MainContainer justifyContent="left">
              <SignStepTypography>Paso 1 de 2</SignStepTypography>
            </MainContainer>
          )}
          <PinWrapper>
            <PinLabel>{pinMessage}</PinLabel>
            <PinContainer>
              <CodeInput
                code={pinState.pin}
                handleCodeChange={handlePinChange}
                numInputs={4}
                isValid={!pinState.error}
                type={"number"}
                secretInputs={true}
              />
              {pinState.error && (
                <StyledErrorMessage>{pinState.errorMessage}</StyledErrorMessage>
              )}
            </PinContainer>
          </PinWrapper>
          <SecondaryActionButton onClick={() => setShowForgottenPinModal(true)}>
            Olvidé mi PIN
          </SecondaryActionButton>

          {/* Creation & Signature Error Modals */}
          <UnknownDocumentCreationErrorModal requestId={requestId} />

          <WrongPinDocumentSignatureErrorModal
            onPrimaryAction={resetPin}
            onSecondaryAction={resetPinAndGoHome}
          />

          <BlockedPinDocumentSignatureErrorModal
            // onPrimaryAction={resetPin}
            onReEnlistment={handleReEnlistment}
            isUnenrolling={isUnenrolling}
            onSecondaryAction={resetPinAndGoHome}
          />

          <UnknownDocumentSignatureErrorModal
            onPrimaryAction={() => signPrescription(pinState.pin, requestId)}
            onSecondaryAction={resetPinAndGoHome}
          />

          <ValidationRequiredDocumentSignatureErrorModal
            onSecondaryAction={resetPinAndGoHome}
          />

          <CertificateRevokedDocumentSignatureErrorModal
            onReEnlistment={handleReEnlistment}
            isUnenrolling={isUnenrolling}
            onSecondaryAction={resetPinAndGoHome}
          />

          {/* Other Modals */}
          <Modal
            title="Restablecer PIN"
            content={
              <>
                Para restablecer tu PIN es necesario volver a{" "}
                <BoldText>verificar tu identidad en 3 pasos</BoldText>
              </>
            }
            isOpen={showForgottenPinModal}
            onClose={() => setShowForgottenPinModal(false)}
            buttonProps={{
              buttonText: "Verificar identidad",
              onClick: handleReEnlistment,
              loading: isUnenrolling,
            }}
            secondaryButtonProps={{
              buttonText: "Volver atrás",
              onClick: () => setShowForgottenPinModal(false),
            }}
          />

          <Modal
            title="Ha ocurrido un error"
            content="No se pudo iniciar el proceso de verificación de identidad. Por favor, inténtalo de nuevo."
            isOpen={showFailedReEnlistmentModal}
            onClose={() => setShowFailedReEnlistmentModal(false)}
            buttonProps={{
              buttonText: "Cerrar",
              onClick: () => setShowFailedReEnlistmentModal(false),
            }}
          />

          <DoctorDocumentReadinessModal {...modalProps} onClose={onReadinessModalClose} />
        </FormBody>
      </FormContentContainer>
    </>
  );
}
